import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, I18nextProvider, initReactI18next } from 'react-i18next';
import i18next from 'i18next';
import hematologyEn from '../../i18n/locales/en/hematology.json';
import hematologyHe from '../../i18n/locales/he/hematology.json';
import { DiseaseId } from './types';
import { diseases } from './data/diseasesData';
import DiseaseCard from './components/DiseaseCard';
import DiseaseDetail from './components/DiseaseDetail';
import { UserInputProvider } from './context/UserInputContext';
import './HematologicalDiseases.scss';

// Dedicated i18n instance for this app — defaults to English regardless of the
// global app language (which defaults to Hebrew). The ?lang= URL parameter is
// respected for demo/presentation use.
const _urlLang = new URLSearchParams(window.location.search).get('lang');
const hematologyI18n = i18next.createInstance();
hematologyI18n
  .use(initReactI18next)
  .init({
    lng: _urlLang || 'en',
    fallbackLng: 'en',
    supportedLngs: ['en', 'he'],
    ns: ['hematology'],
    defaultNS: 'hematology',
    resources: {
      en: { hematology: hematologyEn },
      he: { hematology: hematologyHe },
    },
    interpolation: { escapeValue: false },
  });

// Default font scale — 1.15rem gives slightly larger text than the browser default
const DEFAULT_FONT_SCALE = 1.15;
const CONTAINER_STYLE: React.CSSProperties = { fontSize: `${DEFAULT_FONT_SCALE}rem` };

// Hidden language backdoor: 5 taps on the subtitle within 2 s cycles the language.
// Also respects ?lang= URL parameter for demo/presentation use.
const LANG_CYCLE = ['en', 'he'];
const BACKDOOR_TAPS = 5;
const BACKDOOR_WINDOW_MS = 2000;

const HematologicalDiseasesInner: React.FC = () => {
  const { t, i18n } = useTranslation('hematology');
  const [selectedDisease, setSelectedDisease] = useState<DiseaseId | null>(null);

  const goBack = useCallback(() => setSelectedDisease(null), []);

  useEffect(() => {
    window.addEventListener('breadcrumb-navigate-parent', goBack);
    return () => window.removeEventListener('breadcrumb-navigate-parent', goBack);
  }, [goBack]);

  // ── Hidden language backdoor: 5 taps on the subtitle within 2 s ──
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSubtitleTap = useCallback(() => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, BACKDOOR_WINDOW_MS);
    if (tapCountRef.current >= BACKDOOR_TAPS) {
      tapCountRef.current = 0;
      if (tapTimerRef.current) { clearTimeout(tapTimerRef.current); tapTimerRef.current = null; }
      const current = i18n.language;
      const next = LANG_CYCLE[(LANG_CYCLE.indexOf(current) + 1) % LANG_CYCLE.length];
      i18n.changeLanguage(next);
    }
  }, [i18n]);

  const isRtl = i18n.language === 'he';

  const selectedDiseaseInfo = selectedDisease
    ? diseases.find((d) => d.id === selectedDisease)
    : null;

  const pageSubtitle = selectedDiseaseInfo
    ? `${selectedDiseaseInfo.abbreviation} - ${selectedDiseaseInfo.nameEnglish}`
    : undefined;

  const containerClass = [
    'hematological-diseases-container',
    isRtl ? 'is-rtl' : '',
  ].filter(Boolean).join(' ');

  if (selectedDisease) {
    return (
      <div
        className={containerClass}
        style={CONTAINER_STYLE}
        data-page-title={t('pageTitle')}
        data-page-subtitle={pageSubtitle}
      >
        <DiseaseDetail
          diseaseId={selectedDisease}
          onBack={() => setSelectedDisease(null)}
        />
      </div>
    );
  }

  return (
    <div className={containerClass} style={CONTAINER_STYLE} data-page-title={t('pageTitle')}>
      <div className="diseases-header">
        <div className="diseases-header__text">
          <h1>{t('pageTitle')}</h1>
          {/* 5 taps within 2 s cycles the language (hidden backdoor) */}
          <p onClick={handleSubtitleTap} style={{ cursor: 'default' }}>
            {t('pageSubtitle')}
          </p>
        </div>
      </div>
      <div className="diseases-grid">
        {diseases.map((disease) => (
          <DiseaseCard
            key={disease.id}
            disease={disease}
            onClick={(id) => setSelectedDisease(id as DiseaseId)}
          />
        ))}
      </div>
    </div>
  );
};

const HematologicalDiseases: React.FC = () => (
  <I18nextProvider i18n={hematologyI18n}>
    <UserInputProvider>
      <HematologicalDiseasesInner />
    </UserInputProvider>
  </I18nextProvider>
);

export default HematologicalDiseases;
